<template>
  <div
    :class="[
      'settings-properties',
      {
        'settings-properties--empty': !value.length,
        'settings-properties--sorting-disabled': !sortable,
      },
    ]"
  >
    <div class="settings-properties__title">
      <h3>{{ title }}</h3>
      <HelperPopover v-if="variant === 'order'" class="ml-1" :template="`residentials-settings-${type}-helper`" />
    </div>
    <div class="settings-properties__labels row between middle">
      <span>{{ $t('fieldType') }}</span>
      <CheckboxComponent
        v-if="variant === 'order'"
        v-model="sortable"
        theme="toggle"
        :label="$t('activateCustomSorting')"
      />
      <span v-else>{{ $t('showInUnitList') }}</span>
    </div>
    <Draggable
      v-if="value.length"
      :value="valueComp"
      @input="updateSort"
      class="settings-properties__items"
      handle=".settings-properties__draggable-handle"
      :animation="150"
      :disabled="!sortable"
    >
      <div :class="['settings-properties__row']" v-for="(item, index) in valueComp" :key="index">
        <span>{{ $t(item.label || item.key) }}</span>
        <div v-if="variant === 'order'" class="settings-properties__draggable-handle">
          <FontAwesomeIcon :icon="['fal', 'arrows-up-down']" />
        </div>
        <ChoiceComponent
          v-else
          class="contract-applicant__choice"
          :values="choiceValues"
          :value="item.isFeatured"
          @input="$ev => setFeatured(item, index, $ev)"
        >
        </ChoiceComponent>
      </div>
    </Draggable>

    <EmptyState
      v-show="!value.length"
      :title="$t('noX', { x: title || this.$tc('project', 2).toLowerCase() })"
      :icon="['fal', 'drafting-compass']"
    />
  </div>
</template>

<script>
import Draggable from 'vuedraggable'
import { ChoiceComponent } from 'vue-elder-choice'
import HelperPopover from '@/components/HelperPopover/Component'
import EmptyState from '@/components/EmptyState'
import { CheckboxComponent } from 'vue-elder-checkbox'
import { SortByKey } from '@/utils'

export default {
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    title: String,
    enableSort: Boolean,
    type: {
      type: String,
      default: 'property',
    },
    variant: {
      type: String,
      default: 'order',
      enums: ['order', 'list'],
    },
  },
  watch: {
    enableSort: {
      handler(val) {
        if (val) this.sortable = true
      },
      immediate: true,
    },
    sortable: {
      handler(val) {
        this.updateSort(this.valueComp)
      },
    },
  },
  data() {
    return {
      sortable: false,
      initalValue: [],
    }
  },

  computed: {
    valueComp() {
      let result = this.value
      if (this.sortable) return result.sort(SortByKey('order'))
      return result
    },
    choiceValues() {
      return [
        { value: true, label: this.$t('yes') },
        { value: false, label: this.$t('no') },
      ]
    },
  },
  methods: {
    setFeatured(item, index, value) {
      let result = this.value
      result.splice(index, 1, { ...item, isFeatured: value, order: this.sortable ? index + 1 : 0 })
      this.$emit('input', result)
    },
    updateSort(items) {
      let result = items
      if (!this.sortable) result = this.initalValue
      this.$emit(
        'input',
        result.map((i, index) => ({ ...i, order: this.sortable ? index + 1 : 0 })),
      )
    },
  },
  created() {
    this.initalValue = this.value
    this.updateSort(this.initalValue)
  },
  components: {
    Draggable,
    ChoiceComponent,
    HelperPopover,
    EmptyState,
    CheckboxComponent,
  },
}
</script>

<style lang="scss">
.settings-properties {
  $space: 1rem;

  @mixin item-style {
    background-color: var(--lightest-grey);
    border-radius: var(--border-radius);
    border: 1px solid var((--border-color));
  }

  &--empty {
    .settings-properties__labels {
      opacity: 0;
    }
  }
  &--sorting-disabled {
    .settings-properties__draggable-handle {
      cursor: not-allowed !important;
      opacity: 0.4;
    }
  }

  &__draggable-handle {
    $size: 2rem;
    display: flex;
    gap: 0.5rem;
    align-items: center;
    height: $size;
    width: $size;
    justify-content: center;
    cursor: move;

    @include item-style;
    background-color: var(--light-grey);
  }

  &__labels {
    padding: $space;
    gap: 2rem;
  }
  &__title {
    display: flex;
  }
  &__items {
    max-height: 50vh;
    min-width: 400px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: $space;
  }

  &__row {
    display: flex;
    gap: 2rem;
    align-items: center;
    justify-content: space-between;
    padding: $space;
    @include item-style;
  }

  .elder-checkbox {
    align-items: center;
  }

  .elder-choices {
    margin: 0;
    gap: 0rem;
    padding: 0.25rem;
    background-color: white;
    flex-wrap: nowrap;
    border-radius: var(--border-radius);
    border: 1px solid var((--border-color));

    &__item {
      margin: 0;
      font-weight: normal;
      border: none;
      border-radius: var(--border-radius);
      background-color: white;

      &--active {
        background-color: var(--secondary);
        color: var(--secondary-contrast);
      }

      &-label {
        padding: 0.5rem 1.25rem;
      }
    }
  }

  .empty-state {
    padding: $space;
    @include item-style;
  }
}
</style>
